import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { strapiFetchParameterUjiGridByBakuMutu } from '../../api/strapiCrm';
import { BakuMutuMasterAutocomplete } from '../../components/BakuMutuMasterAutocomplete';
import { JenisPengukuranMasterAutocomplete } from '../../components/JenisPengukuranMasterAutocomplete';
import { ParameterUjiByBakuMutuGrid } from '../../components/ParameterUjiByBakuMutuGrid';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../ui/CollapsibleKeteranganSplit';
import { queryKeys } from '../../query/queryKeys';
import { useCreateSampleInformationMutation, useWorkOrderDetailQuery } from '../../query/hooks';
import { formatParameterSelectionSummaryFromGridRows } from '../../utils/sampleParameterDisplay';
import { useQuery } from '@tanstack/react-query';

export function WorkOrderSampleCreateRoute() {
  const { jwt } = useAuth();
  const navigate = useNavigate();
  const { documentId: rawId } = useParams<{ documentId: string }>();
  const documentId = rawId ? decodeURIComponent(rawId) : null;

  const detailQ = useWorkOrderDetailQuery(jwt, documentId);
  const createMut = useCreateSampleInformationMutation(jwt);

  const [jenisMaster, setJenisMaster] = useState<DynamicEntityRow | null>(null);
  const [bakuMaster, setBakuMaster] = useState<DynamicEntityRow | null>(null);
  const [parameterUji, setParameterUji] = useState('');
  const [jumlahTitik, setJumlahTitik] = useState('1');
  const [harga, setHarga] = useState('');

  const bmid = bakuMaster?.documentId?.trim();
  const gridRowsQ = useQuery({
    queryKey: queryKeys.parameterUjiGridByBakuMutu(jwt, bmid || null),
    queryFn: () => strapiFetchParameterUjiGridByBakuMutu(jwt!, bmid!),
    enabled: Boolean(jwt && bmid),
  });
  const parameterRingkas = useMemo(
    () => formatParameterSelectionSummaryFromGridRows(gridRowsQ.data ?? [], parameterUji),
    [gridRowsQ.data, parameterUji],
  );

  useDocumentTitle(appDocumentTitle('Tambah sampel'));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const quotationId = detailQ.data?.quotation?.documentId;
    if (!jwt || !documentId || !quotationId) return;
    const jt = Number.parseInt(jumlahTitik, 10);
    if (!Number.isFinite(jt) || jt < 1) return;
    createMut.reset();
    const hTrim = harga.trim();
    const hNum = hTrim === '' ? null : Number(hTrim);
    if (hTrim !== '' && !Number.isFinite(hNum)) return;
    const jpId = jenisMaster?.documentId?.trim() ?? '';
    const bmId = bakuMaster?.documentId?.trim() ?? '';
    if (!jpId || !bmId) return;
    try {
      await createMut.mutateAsync({
        quotation: { connect: [quotationId] },
        jenis_pengukuran: jpId,
        parameter_uji: parameterUji.trim(),
        baku_mutu: bmId,
        sample_point_count: jt,
        harga: hNum,
      });
      navigate(`/sampling/work-order/${encodeURIComponent(documentId)}/view`);
    } catch {
      /* surfaced below */
    }
  }

  if (!documentId) {
    return (
      <PageShell title="Tambah sampel">
        <Alert severity="warning">ID work order tidak valid.</Alert>
        <Button component={RouterLink} to="/sampling/work-orders" sx={{ mt: 2 }}>
          Kembali
        </Button>
      </PageShell>
    );
  }

  if (detailQ.isPending) {
    return (
      <PageShell fill title="Tambah sampel">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </PageShell>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <PageShell title="Tambah sampel">
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat work order.'}
        </Alert>
        <Button component={RouterLink} to="/sampling/work-orders">
          Kembali
        </Button>
      </PageShell>
    );
  }

  const wo = detailQ.data;
  const q = wo.quotation;
  const qDocId = q?.documentId;
  const hTrim = harga.trim();
  const hNum = hTrim === '' ? null : Number(hTrim);
  const hargaInvalid = hTrim !== '' && !Number.isFinite(hNum);
  const jt = Number.parseInt(jumlahTitik, 10);
  const jumlahInvalid = !Number.isFinite(jt) || jt < 1;

  return (
    <PageShell fill title="Tambah sampel">
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <CollapsibleKeteranganSplit
          toggleTestId="work-order-sample-create-info"
          keterangan={
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Work order
                {q ? (
                  <>
                    {' '}
                    · <strong>{q.quotation_number}</strong>
                  </>
                ) : null}
              </Typography>
              {createMut.isError ? (
                <Alert severity="error">
                  {createMut.error instanceof Error
                    ? createMut.error.message
                    : 'Gagal menyimpan sampel.'}
                </Alert>
              ) : null}
            </Stack>
          }
        >
          <Stack spacing={2} sx={{ maxWidth: 960 }}>
            <JenisPengukuranMasterAutocomplete
              value={jenisMaster}
              onChange={setJenisMaster}
              required
              inputTestId="sample-form-matriks"
            />
            <BakuMutuMasterAutocomplete
              value={bakuMaster}
              onChange={(v) => {
                if (v?.documentId?.trim() !== bakuMaster?.documentId?.trim()) {
                  setParameterUji('');
                }
                setBakuMaster(v);
              }}
              required
              inputTestId="sample-form-regulasi"
            />
            <ParameterUjiByBakuMutuGrid
              jwt={jwt}
              bakuMutuDocumentId={bakuMaster?.documentId}
              value={parameterUji}
              onChange={setParameterUji}
              disabled={!bakuMaster?.documentId?.trim()}
              gridTestId="work-order-sample-create-parameter-grid"
              rowCheckboxTestId={(i) => `work-order-sample-create-parameter-cb-${i}`}
            />
            <TextField
              label="Parameter uji"
              value={parameterRingkas}
              fullWidth
              size="small"
              disabled
              inputProps={{ 'data-testid': 'work-order-sample-create-param-summary' }}
            />
            <TextField
              label="Jumlah titik"
              type="number"
              value={jumlahTitik}
              onChange={(ev) => setJumlahTitik(ev.target.value)}
              required
              fullWidth
              size="small"
              inputProps={{ min: 1, 'data-testid': 'sample-form-jumlah-titik' }}
              error={jumlahTitik !== '' && jumlahInvalid}
              helperText={jumlahTitik !== '' && jumlahInvalid ? 'Minimal 1' : undefined}
            />
            <TextField
              label="Harga (opsional)"
              type="number"
              value={harga}
              onChange={(ev) => setHarga(ev.target.value)}
              fullWidth
              size="small"
              inputProps={{ 'data-testid': 'sample-form-harga' }}
              error={hargaInvalid}
              helperText={hargaInvalid ? 'Isi angka valid atau kosongkan' : undefined}
            />
          </Stack>
        </CollapsibleKeteranganSplit>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={
              createMut.isPending ||
              !jenisMaster?.documentId?.trim() ||
              !bakuMaster?.documentId?.trim() ||
              !parameterUji.trim() ||
              jumlahInvalid ||
              hargaInvalid
            }
            data-testid="sample-form-submit"
          >
            {createMut.isPending ? 'Menyimpan…' : 'Simpan sampel'}
          </Button>
          <Button component={RouterLink} to={`/sampling/work-order/${encodeURIComponent(documentId)}/view`}>
            Batal
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}
